// frontend/pages/Invoices.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  DocumentArrowDownIcon, 
  EnvelopeIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { transactionService } from '../services/api';
import DataTable from '../components/Common/DataTable';
import Modal from '../components/Common/Modal';
import InvoiceForm from '../components/Invoices/InvoiceForm';
import InvoiceDetails from '../components/Invoices/InvoiceDetails';
import { toast } from 'react-hot-toast';

export default function Invoices() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => transactionService.getInvoices(filters)
  });

  const sendReminderMutation = useMutation({
    mutationFn: (invoiceId) => transactionService.sendReminder(invoiceId),
    onSuccess: () => {
      toast.success('Reminder sent successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to send reminder');
    }
  });

  const columns = [
    { key: 'invoice_number', header: 'Invoice #', sortable: true },
    { key: 'customer_name', header: 'Customer', sortable: true },
    { key: 'created_at', header: 'Date', sortable: true, format: 'date' },
    { key: 'due_date', header: 'Due Date', sortable: true, format: 'date' },
    { 
      key: 'total_amount', 
      header: 'Amount', 
      sortable: true, 
      format: 'currency',
      render: (value) => `KES ${value?.toLocaleString()}`
    },
    { 
      key: 'amount_paid', 
      header: 'Paid', 
      sortable: true, 
      format: 'currency',
      render: (value) => `KES ${value?.toLocaleString()}`
    },
    { 
      key: 'balance_due', 
      header: 'Balance', 
      sortable: true, 
      format: 'currency',
      render: (value) => `KES ${value?.toLocaleString()}`
    },
    { 
      key: 'has_services', 
      header: 'Type', 
      sortable: false,
      render: (_, row) => {
        if (row.has_services && row.has_products) {
          return (
            <div className="flex items-center space-x-1">
              <ShoppingBagIcon className="h-4 w-4 text-blue-500" />
              <WrenchScrewdriverIcon className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-gray-500">Mixed</span>
            </div>
          );
        } else if (row.has_services) {
          return (
            <div className="flex items-center space-x-1">
              <WrenchScrewdriverIcon className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-purple-600">Service</span>
            </div>
          );
        } else {
          return (
            <div className="flex items-center space-x-1">
              <ShoppingBagIcon className="h-4 w-4 text-blue-500" />
              <span className="text-xs text-blue-600">Product</span>
            </div>
          );
        }
      }
    },
    { 
      key: 'status', 
      header: 'Status', 
      sortable: true,
      render: (value) => {
        const statusColors = {
          paid: 'bg-green-100 text-green-800',
          pending: 'bg-yellow-100 text-yellow-800',
          partial: 'bg-blue-100 text-blue-800',
          overdue: 'bg-red-100 text-red-800',
          draft: 'bg-gray-100 text-gray-800'
        };
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${statusColors[value]}`}>
            {value.toUpperCase()}
          </span>
        );
      }
    }
  ];

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailsModal(true);
  };

  const handleSendReminder = (invoice) => {
    if (window.confirm(`Send payment reminder to ${invoice.customer_name}?`)) {
      sendReminderMutation.mutate(invoice.id);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const pdfBlob = await transactionService.downloadInvoice(invoice.id);
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download invoice');
    }
  };

  const actions = [
    {
      label: 'View',
      onClick: handleViewInvoice,
      icon: 'eye'
    },
    {
      label: 'Send Reminder',
      onClick: handleSendReminder,
      icon: 'mail',
      show: (row) => row.status === 'pending' || row.status === 'partial'
    },
    {
      label: 'Download PDF',
      onClick: handleDownloadPDF,
      icon: 'download'
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600">Manage and track all invoices</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Invoice
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search by invoice # or customer..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="overdue">Overdue</option>
          </select>
          <input
            type="date"
            placeholder="Start Date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="date"
            placeholder="End Date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        actions={actions}
        pagination={data?.pagination}
      />

      {/* Create Invoice Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Invoice"
        size="large"
      >
        <InvoiceForm onSuccess={() => {
          setShowCreateModal(false);
          queryClient.invalidateQueries(['invoices']);
        }} />
      </Modal>

      {/* Invoice Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Invoice Details"
        size="large"
      >
        {selectedInvoice && (
          <InvoiceDetails 
            invoice={selectedInvoice} 
            onClose={() => setShowDetailsModal(false)}
            onUpdate={() => {
              queryClient.invalidateQueries(['invoices']);
              setShowDetailsModal(false);
            }}
          />
        )}
      </Modal>
    </div>
  );
}